import { AfterViewInit, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { HolidayTemplateEntryModel } from '../../../../../core/models/settings/holiday/holiday-template-entry.model';
import { GridComponent } from '@progress/kendo-angular-grid';
import { faCircleMinus, faPen, faTreeChristmas } from '@fortawesome/pro-light-svg-icons';
import { UserOperation } from '../../../../../core/enums/user-operation';
import { combineLatestWith, filter, take } from 'rxjs/operators';
import { HolidayTemplateModel } from '../../../../../core/models/settings/holiday/holiday-template.model';
import { SubSink } from 'subsink';
import { HolidaySettingsHolidayDialogComponent } from '../dialogs/holiday-settings-holiday-dialog/holiday-settings-holiday-dialog.component';
import { ShiveDialogService } from '../../../../../core/services/controls/shive-dialog.service';
import { HolidaySettingsStore } from '../../state-and-data-handling/holiday-settings.store';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { cloneDeep } from 'lodash-es';
import { ShiveGridWrapperComponent } from '../../../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { generateDummyId } from '../../../../../shared/functions/array-functions';
import { Logger } from '../../../../../shared/logging/logger';

@Component({
  selector: 'app-holiday-settings-holiday-grid',
  templateUrl: './holiday-settings-holiday-grid.component.html',
  standalone: false,
})
export class HolidaySettingsHolidayGridComponent implements AfterViewInit, OnDestroy {
  @Input() holidays: HolidayTemplateEntryModel[];
  @Input() holidayListId: number;
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;
  @ViewChild('grid') grid: GridComponent;
  public readonly UserOperation = UserOperation;
  public readonly faTreeChristmas = faTreeChristmas;
  private readonly subs = new SubSink();

  constructor(
    private readonly holidaySettingsStore: HolidaySettingsStore,
    private readonly shiveDialogService: ShiveDialogService,
  ) {}

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public upsertHoliday(
    userOperation: UserOperation,
    existingHoliday?: HolidayTemplateEntryModel,
  ): void {
    const dialogRef = this.buildDialog(userOperation);

    const holidaySettingsHolidayDialogComponent = dialogRef.content
      .instance as HolidaySettingsHolidayDialogComponent;
    holidaySettingsHolidayDialogComponent.userOperation = userOperation;
    if (userOperation === UserOperation.Update && existingHoliday) {
      holidaySettingsHolidayDialogComponent.holidayId = existingHoliday.id;
      holidaySettingsHolidayDialogComponent.holidayName = existingHoliday.name;
      holidaySettingsHolidayDialogComponent.holidayDate = existingHoliday.date;
    }
    holidaySettingsHolidayDialogComponent.holidays = [...this.holidays];

    this.subs.sink = dialogRef.result
      .pipe(
        filter((value) => value !== UserOperation.Cancel),
        combineLatestWith(
          this.holidaySettingsStore.selectSingleHolidayTemplate(this.holidayListId).pipe(take(1)),
        ),
        take(1),
      )
      .subscribe(([result, holidayTemplateModel]: [[string, Date], HolidayTemplateModel]) => {
        const clonedHolidayTemplateModel = cloneDeep(holidayTemplateModel);
        const [holidayName, holidayDate] = result;
        if (userOperation === UserOperation.Create) {
          this.insertHoliday(clonedHolidayTemplateModel, holidayName, holidayDate);
        } else if (userOperation === UserOperation.Update) {
          this.updateHoliday(
            clonedHolidayTemplateModel,
            holidayName,
            holidayDate,
            existingHoliday!.id,
          );
        } else {
          throw new Error('Illegal user operation.');
        }

        this.holidays = [...clonedHolidayTemplateModel.holidayEntries];
        Logger.debug('Template updated in upsertHoliday()', clonedHolidayTemplateModel);
        this.holidaySettingsStore.updateHolidayTemplate(clonedHolidayTemplateModel);
        this.holidaySettingsStore.userOperationConducted.next(true);
      });
  }

  private initContextMenuCallbacks(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: (holiday: HolidayTemplateEntryModel) => {
          this.upsertHoliday(UserOperation.Update, holiday);
        },
        isVisible: true,
        isActivated: true,
      },
      {
        text: 'Entfernen',
        faIcon: faCircleMinus,
        callback: (holiday: HolidayTemplateEntryModel) => {
          this.removeHoliday(holiday.id);
        },
        isVisible: true,
        isActivated: true,
      },
    ];
  }

  private insertHoliday(
    updatedHolidayTemplateModel: HolidayTemplateModel,
    holidayName: string,
    holidayDate: Date,
  ): void {
    const holidayTemplateNameModel = new HolidayTemplateEntryModel();
    holidayTemplateNameModel.id = generateDummyId([...updatedHolidayTemplateModel.holidayEntries]);
    holidayTemplateNameModel.name = holidayName;
    holidayTemplateNameModel.date = holidayDate;
    updatedHolidayTemplateModel.holidayEntries = [
      holidayTemplateNameModel,
      ...updatedHolidayTemplateModel.holidayEntries,
    ];
  }

  private updateHoliday(
    updatedHolidayTemplateModel: HolidayTemplateModel,
    updatedHolidayName: string,
    updatedHolidayDate: Date,
    holidayId: number,
  ) {
    const editedHolidayIndex = updatedHolidayTemplateModel.holidayEntries.findIndex(
      (holiday) => holiday.id === holidayId,
    );
    if (editedHolidayIndex < 0) {
      throw new Error('Holiday could not be found.');
    }
    updatedHolidayTemplateModel.holidayEntries[editedHolidayIndex].name = updatedHolidayName;
    updatedHolidayTemplateModel.holidayEntries[editedHolidayIndex].date = updatedHolidayDate;
  }

  private removeHoliday(holidayId: number) {
    this.subs.sink = this.holidaySettingsStore
      .selectSingleHolidayTemplate(this.holidayListId)
      .pipe(take(1))
      .subscribe((holidayTemplateModel) => {
        const clonedHolidayTemplateModel = cloneDeep(holidayTemplateModel)!;
        const clonedHolidays = cloneDeep([...clonedHolidayTemplateModel.holidayEntries]);
        const removedHolidayIndex = clonedHolidayTemplateModel.holidayEntries.findIndex(
          (holiday) => holiday.id === holidayId,
        );
        if (removedHolidayIndex < 0) {
          throw new Error('Holiday could not be found.');
        }
        clonedHolidays.splice(removedHolidayIndex, 1);
        clonedHolidayTemplateModel.holidayEntries = clonedHolidays;
        this.holidays = [...clonedHolidayTemplateModel.holidayEntries];
        Logger.debug('Template updated in upsertHoliday()', clonedHolidayTemplateModel);
        this.holidaySettingsStore.updateHolidayTemplate(clonedHolidayTemplateModel);
        this.holidaySettingsStore.userOperationConducted.next(true);
      });
  }

  private buildDialog(userOperation: UserOperation): DialogRef {
    return this.shiveDialogService.open({
      content: HolidaySettingsHolidayDialogComponent,
      width: 572,
      title: userOperation === UserOperation.Create ? 'Feiertag hinzufügen' : 'Feiertag editieren',
    });
  }
}
